<template>

    <!-- Navigation -->
    <header class="nav">
      <router-link class="nav-brand" to="/">
        <span v-html="config.site.siteName"></span>
      </router-link>
      <nav class="nav-links">
        <router-link to="/products"><span v-html="config.navigation.produkte"></span></router-link>

        <router-link v-if="isLoggedIn" to="UserProfile"><span v-html="config.navigation.benutzerprofil"></span></router-link>

        <router-link v-if="isAdmin" to="/admin" class="admin-link"><span v-html="config.navigation.admin"></span></router-link>
      </nav>
    </header>

    <!-- Page content -->
    <main class="content">
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="footer">
      <router-link class="footer-link" :to="{name: 'KontaktPage'}">
        <span v-html="config.navigation.kontakt || 'Kontakt'"></span>
      </router-link>
      <span v-html="config.site.footerText"></span>
      <router-link class="footer-link" :to="{name: 'ImpressumPage'}">
        <span v-html="config.navigation.impressum"></span>
      </router-link>
    </footer>
</template>

<script>
import { useConfig } from './composables/useConfig.js';

export default {
  name: 'App',
  setup() {
    const { config, loadConfig, applyColors } = useConfig();
    
    loadConfig().then(() => applyColors());
    
    return { config };
  },
  data() {
    return {
      isLoggedIn: false,
      isAdmin: false
    };
  },
  mounted() {
    this.checkLoginStatus();
    this.applyMetadata();
    window.addEventListener('auth-changed', this.checkLoginStatus);
    window.addEventListener('config-updated', this.applyMetadata);
  },
  beforeUnmount() {
    window.removeEventListener('auth-changed', this.checkLoginStatus);
    window.removeEventListener('config-updated', this.applyMetadata);
  },
  watch: {
    'config.metadata': {
      deep: true,
      handler() {
        this.applyMetadata();
      }
    }
  },
  methods: {
    checkLoginStatus() {
      this.isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
      this.isAdmin = sessionStorage.getItem('isAdmin') === 'true';
    },
    applyMetadata() {
      const meta = (this.config && this.config.metadata) ? this.config.metadata : {};

      if (meta.pageTitle) {
        document.title = meta.pageTitle;
      }

      if (meta.favicon) {
        let favicon = document.querySelector('link[rel="icon"]');
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.setAttribute('rel', 'icon');
          document.head.appendChild(favicon);
        }
        favicon.setAttribute('href', meta.favicon);
      }

      if (meta.lang) {
        document.documentElement.setAttribute('lang', meta.lang);
      }

      const setMeta = (name, content, isProperty = false) => {
        if (!content) return;
        const attr = isProperty ? 'property' : 'name';
        let tag = document.querySelector(`meta[${attr}="${name}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(attr, name);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };

      setMeta('description', meta.description);
      setMeta('keywords', meta.keywords);
      setMeta('author', meta.author);
      setMeta('robots', meta.robots);
      setMeta('theme-color', meta.themeColor);

      if (meta.canonicalUrl) {
        let link = document.querySelector('link[rel="canonical"]');
        if (!link) {
          link = document.createElement('link');
          link.setAttribute('rel', 'canonical');
          document.head.appendChild(link);
        }
        link.setAttribute('href', meta.canonicalUrl);
      }

      setMeta('og:type', meta.ogType || 'website', true);
      setMeta('og:url', meta.ogUrl, true);
      setMeta('og:title', meta.ogTitle || meta.pageTitle, true);
      setMeta('og:description', meta.ogDescription || meta.description, true);
      if (meta.ogImage) setMeta('og:image', meta.ogImage, true);

      setMeta('twitter:card', meta.twitterCard || 'summary_large_image');
      setMeta('twitter:title', meta.ogTitle || meta.pageTitle);
      setMeta('twitter:description', meta.ogDescription || meta.description);
      if (meta.ogImage) setMeta('twitter:image', meta.ogImage);
    }
  }
};
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Amatic+SC:wght@400;700&family=Anton&family=Archivo:wght@400;700&family=Bebas+Neue&family=Bitter:wght@400;700&family=Caveat:wght@400;700&family=Cinzel:wght@400;700&family=Cormorant+Garamond:wght@400;700&family=DM+Sans:wght@400;700&family=Dancing+Script:wght@400;700&family=Fira+Sans:wght@400;700&family=Inter:wght@400;700&family=Josefin+Sans:wght@400;700&family=Karla:wght@400;700&family=Lato:wght@400;700&family=Libre+Baskerville:wght@400;700&family=Lora:wght@400;700&family=Manrope:wght@400;700&family=Merriweather:wght@400;700&family=Montserrat:wght@400;700&family=Nunito:wght@400;700&family=Open+Sans:wght@400;700&family=Oswald:wght@400;700&family=Pacifico&family=Playfair+Display:wght@400;700&family=Poppins:wght@400;700&family=PT+Serif:wght@400;700&family=Quicksand:wght@400;700&family=Raleway:wght@400;700&family=Roboto+Slab:wght@400;700&family=Roboto:wght@400;700&family=Rubik:wght@400;700&family=Source+Sans+3:wght@400;700&family=Work+Sans:wght@400;700&display=swap');

/* Base reset */
html, body {
  margin: 0;
  padding: 0;
  font-family: Avenir, Helvetica, Arial, sans-serif;
  background-color: var(--page-background-color, #ffffff);
  background-image: var(--page-background-image, none);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
  background-attachment: fixed;
}
/* App shell */
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  margin-top: 0;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.content {
  flex-grow: 1;
}

h1, h2, h3, h4, h5, h6 {
  font-family: inherit;
}

/* Navigation */
.nav {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  background-color: #5d4037;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1000;
}

.nav-brand {
  font-size: 1.5rem;
  font-weight: bold;
  color: #f5e9d4;
  text-decoration: none;
}

.nav-brand.router-link-exact-active {
  color: #f5e9d4;
}

.nav-links {
  display: flex;
  gap: 1.5rem;
}

.nav-links a {
  font-weight: bold;
  color: #f5e9d4;
  text-decoration: none;
  padding: 5px 0;
  transition: color 0.3s, border-bottom 0.3s;
}

.nav-links a:hover {
  color: white;
  border-bottom: 2px solid white;
}

.nav-links a.router-link-exact-active {
  color: white;
  border-bottom: 2px solid #f5e9d4;
}

/* Footer */
.footer {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 0.5rem 1.5rem;
  font-size: 0.85rem;
  background: #2d1b14;
  color: #f5e9d4;
}

.footer-link {
  color: #f5e9d4;
  text-decoration: none;
}

.footer-link.router-link-active {
  text-decoration: underline;
}
</style>